import { Component, computed, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { debounceTime } from 'rxjs/operators'
import { MapGeoJSONFeature } from 'maplibre-gl'
import { MapEventsService } from 'src/app/core/services/map-events.service'
import { Layers } from 'src/app/shared/layers-config/layers'
import { DetailsItemModel, DetailsModel } from 'src/app/shared/models/details.model'
import { format } from 'date-fns'
import { ObservationProperties, SupplementalSign } from 'src/app/shared/models/observation.model'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { AppPaths } from 'src/app/app-routing.module'
import { getTimeZoneLabel } from 'src/app/shared/utils/date.utils'
import { MapSignsIcons, MapSupsIcons } from 'src/app/core/enums/map-icons.enum'
import { parseIfString } from 'src/app/shared/utils/json.utils'

@Component({
  selector: 'app-observations-details',
  templateUrl: './observations-details.component.html',
  standalone: false,
})
export class ObservationsDetailsComponent {
  public readonly details = signal(new Map<string, DetailsModel>())
  public readonly properties = signal<ObservationProperties | undefined>(undefined)
  public readonly rawFeature = computed(() =>
    JSON.stringify(this.features()[this.index()], undefined, 2),
  )
  public readonly features = signal<MapGeoJSONFeature[]>([])
  public readonly zoomLevel = signal<number | undefined>(undefined)
  public readonly index = signal(0)
  public readonly selectedFeatureId = signal('')

  constructor(
    private readonly mapEventService: MapEventsService,
    private appState: AppStateService,
  ) {
    this.mapEventService.featureClick
      .pipe(takeUntilDestroyed())
      .pipe(debounceTime(50))
      .subscribe((features: MapGeoJSONFeature[]) => {
        this.clearDetails()
        if (this.features().length != features.length) {
          this.index.set(0)
        }
        if (
          features[this.index()].layer.id === Layers.Provider1Observations.name ||
          features[this.index()].layer.id === Layers.Provider2Observations.name
        ) {
          this.features.set(features)
          this.setDetails()
        }
      })
  }

  toggleFeature(index: number) {
    this.index.set(index)
    this.setDetails()
  }

  setDetails() {
    this.selectedFeatureId.set(this.features()[this.index()].id?.toString() ?? '')

    const raw = this.features()[this.index()].properties as ObservationProperties
    const properties: ObservationProperties = {
      ...raw,
      supplementalSign: parseIfString(raw.supplementalSign),
    }
    this.properties.set(properties)

    this.zoomLevel.set(this.appState.getState().zoom)
    this.mapObservationsDetails(properties)
    this.appState.setState(AppPaths.Internal, {
      selectedFeatureId: this.selectedFeatureId(),
    })
  }

  mapObservationsDetails(properties: ObservationProperties) {
    this.setDetail('Properties', {
      title: 'Observation',
      items: [
        {
          description: 'ID',
          value: this.selectedFeatureId(),
        },
        {
          description: 'DisplayType',
          value: properties.displayType!,
        },
        {
          description: 'Source',
          value: properties.source!,
          blur: true,
        },
        {
          description: 'MainType',
          value: properties.mainType?.toString() ?? '',
        },
        {
          description: 'SpeedLimit',
          value: properties.speedLimit?.toString() ?? 'null',
        },

        {
          description: `Valid time (${getTimeZoneLabel(new Date(properties.sourceTime))})`,
          value: `${
            properties.sourceTime
              ? format(new Date(properties.sourceTime), 'yyyy-MM-dd HH:mm')
              : '-'
          }`,
        },
        {
          description: 'Supplementary',
          value: JSON.stringify(properties.supplementalSign ?? '[]'),
        },
      ],
    })
    this.setDetail('Sign', {
      title: 'Sign',
      items: this.getSignItem(properties),
    })
    this.setDetail('Supplementary Signs', {
      title: 'Supplementary Sign',
      items: this.getSupItems(properties.supplementalSign),
    })
  }
  getSupItems(supplementalSigns: SupplementalSign[]): DetailsItemModel[] {
    const items: DetailsItemModel[] = []
    supplementalSigns = supplementalSigns || []
    supplementalSigns.forEach((sign) => {
      if (sign.type === 'UNKNOWN_TYPE') {
        items.push({
          description: MapSupsIcons.SUP_UNKNOWN,
          value: sign.value ?? '',
        })
      } else {
        items.push({
          description: sign.type,
          value: sign.value ?? '',
        })
      }
    })
    return items
  }

  getSignItem(properties: ObservationProperties): DetailsItemModel[] {
    let description = properties.mainType.includes(MapSignsIcons.SPEED_LIMIT)
      ? properties.mainType + '_' + (properties.speedLimit ?? 'NULL')
      : properties.mainType

    // Add _DIGITAL suffix if displayType is digital
    if (properties.displayType?.toLowerCase() === 'digital') {
      description += '_DIGITAL'
    }

    const item: DetailsItemModel = {
      description: description,
      value: properties.speedLimit?.toString() ?? '',
    }

    return [item]
  }
  getSupplementaryFontSize(value: string): string {
    // Adjust font size based on value
    const length = value?.toString().length || 0
    if (length >= 10) return '10px'
    return '16px'
  }
  close() {
    this.clearDetails()
    this.appState.setState(AppPaths.Internal, {
      selectedFeatureId: undefined,
    })
  }

  private setDetail(key: string, value: DetailsModel): void {
    this.details.update((details) => new Map(details).set(key, value))
  }

  private clearDetails(): void {
    this.details.set(new Map())
  }
}
